// ABOUTME: Detail view for a single clicked pivot row — mockup-style KPI cards, an overlaid daily chart
// ABOUTME: with pinnable peer lines (search/dropdown + ranking), a peer ranking, weekday pattern and breakdown.
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";
import Subheading from "../../../../theme/text/Subheading";
import Heading from "../../../../theme/text/Heading";
import { LineChartContentReports } from "../../../../theme/charts/LineChart";
import { DropdownBaseV2 } from "../../../../theme/dropdowns/Dropdown";
import { rhChartColorsDark, rhChartColorsLight } from "../../chartFilterEnums";
import { purgeNonSerializable } from "../../reportUtils";
import {
    useGetBiPivotRowDetailQuery,
    useLazyGetBiPivotRowDetailQuery,
} from "../../../../../newapi/global/businessIntelligence";
import { RhCrossDomainSidebar } from "./RhCrossDomainSidebar";

const MAX_PIN = 4;
const round1 = (n) => Math.round(n * 10) / 10;
const signed = (n) => `${n >= 0 ? "+" : ""}${round1(n)}`;
// 2024-01-07 is a Sunday; offsetting by a weekday index yields that weekday for locale short names.
const weekdayName = (wd, locale) =>
    new Date(2024, 0, 7 + wd).toLocaleDateString(locale, { weekday: "short" });

export const RhPivotRowDetail = ({
    currentReport,
    row,
    chartOption,
    allRows = [],
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";
    const chartColors = isDark ? rhChartColorsDark : rhChartColorsLight;

    const [secondaryDimension, setSecondaryDimension] = useState(null);
    const [pinned, setPinned] = useState([]); // [{ id, name, current[] }]

    useEffect(() => {
        setPinned([]);
        setSecondaryDimension(null);
    }, [row?.id]);

    const drillable = row && row.id !== undefined && row.id !== null;

    const body = useMemo(() => {
        if (!drillable) return null;
        return {
            ...purgeNonSerializable(currentReport),
            reportType: "pivotdetail",
            detail: { id: row.id, name: row.name },
            secondaryDimension,
        };
    }, [currentReport, row, secondaryDimension, drillable]);

    const { data, isFetching, isError } = useGetBiPivotRowDetailQuery(body, {
        skip: !body,
    });
    const [triggerPin] = useLazyGetBiPivotRowDetailQuery();
    const detail = data?.detail;
    const selfId = detail?.entity?.id;

    const fetchSeries = async (id, name) => {
        const res = await triggerPin({ ...body, detail: { id, name } });
        const cur = res?.data?.detail?.series?.current;
        return cur ? { id, name, current: cur } : null;
    };

    // Reconcile the pinned set against a desired list of ids (from the dropdown or a bar click).
    const syncPins = async (ids) => {
        const wanted = ids.filter((id) => id !== selfId).slice(0, MAX_PIN);
        const keep = pinned.filter((p) => wanted.includes(p.id));
        const toAdd = wanted.filter((id) => !pinned.some((p) => p.id === id));
        if (!toAdd.length) {
            setPinned(keep);
            return;
        }
        const added = await Promise.all(
            toAdd.map((id) =>
                fetchSeries(id, allRows.find((r) => r.id === id)?.name)
            )
        );
        setPinned([...keep, ...added.filter(Boolean)].slice(0, MAX_PIN));
    };

    const togglePin = (entity) => {
        if (entity.id === selfId) return;
        const next = pinned.some((p) => p.id === entity.id)
            ? pinned.filter((p) => p.id !== entity.id).map((p) => p.id)
            : [...pinned.map((p) => p.id), entity.id];
        syncPins(next);
    };

    const chartSeries = useMemo(() => {
        if (!detail) return [];
        return [
            { name: t("current_period"), data: detail.series.current },
            { name: t("vs_last_year"), data: detail.series.lastYear },
            { name: t("vs_prev_period"), data: detail.series.previous },
            ...pinned.map((p) => ({ name: p.name, data: p.current })),
        ];
    }, [detail, pinned, t]);

    const weekday = useMemo(() => {
        if (!detail || detail.window.frame !== "1D") return null;
        const start = new Date(detail.window.start);
        const sums = Array(7).fill(0);
        const counts = Array(7).fill(0);
        detail.series.current.forEach((v, i) => {
            if (v == null) return;
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            sums[d.getDay()] += v;
            counts[d.getDay()] += 1;
        });
        const avgs = sums.map((s, i) =>
            counts[i] ? round1(s / counts[i]) : 0
        );
        const active = avgs
            .map((a, i) => ({ wd: i, a }))
            .filter((x) => counts[x.wd] > 0);
        if (!active.length) return null;
        return {
            avgs,
            max: Math.max(...avgs, 1),
            best: active.reduce((m, x) => (x.a > m.a ? x : m)).wd,
            worst: active.reduce((m, x) => (x.a < m.a ? x : m)).wd,
        };
    }, [detail]);

    const breakdownOptions = useMemo(
        () =>
            (chartOption?.pivotTableOptions ?? []).filter(
                (d) => d !== currentReport?.table?.row
            ),
        [chartOption, currentReport]
    );
    const pinOptions = useMemo(
        () => allRows.filter((r) => r.id !== selfId),
        [allRows, selfId]
    );

    const cardBg = isDark ? colors.grey[300] : colors.grey[100];

    // 'YYYY-MM-DD' -> 'DD-MM-YY' and a "start – end" range, so each card shows the period it compares against.
    const fmtD = (s) =>
        s ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(2, 4)}` : "";
    const fmtRange = (p) => (p ? `${fmtD(p.start)} – ${fmtD(p.end)}` : "");
    const periods = detail?.periods || {};
    const projDelta =
        detail?.pace && detail.kpi.comparisons.previous.total > 0
            ? round1(
                  ((detail.pace.projectedTotal -
                      detail.kpi.comparisons.previous.total) /
                      detail.kpi.comparisons.previous.total) *
                      100
              )
            : null;

    // Dashboard-style card chrome (matches the warehouse "te verwerken" widgets).
    const cardSx = {
        flex: "1 1 320px",
        minWidth: 300,
        background: isDark ? colors.grey[200] : colors.grey[0],
        border: isDark ? "none" : `1px solid ${colors.grey[100]}`,
        boxShadow: isDark ? "none" : `0px 2px 4px ${colors.grey[200]}`,
        borderRadius: 4,
        p: 1,
        pb: 1.5,
        px: 1.5,
        display: "flex",
        flexDirection: "column",
    };
    const greyTint = isDark ? colors.grey[400] : colors.grey[100];
    const pct = (d) =>
        d == null ? "" : `${d >= 0 ? "↑" : "↓"} ${Math.abs(d)}%`;

    // Header: title + subtitle on the left, a big number on the right.
    const CardHeader = ({ title, sub, big, bigColor }) => (
        <Box
            sx={{
                p: 1,
                px: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <Text noBreak>{title}</Text>
                {sub && (
                    <Text light noBreak>
                        {sub}
                    </Text>
                )}
            </Box>
            <Typography
                sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 21,
                    fontWeight: 1000,
                    lineHeight: "21px",
                    whiteSpace: "nowrap",
                    color: bigColor ?? colors.txt.primary,
                }}
            >
                {big}
            </Typography>
        </Box>
    );

    // Tinted sub-box: number on top, label below (label colour via `variant`, e.g. 'green' / 'red').
    const MiniBox = ({ num, label, tintBg, variant }) => (
        <Box
            sx={{
                flex: 1,
                overflow: "hidden",
                borderRadius: 3,
                px: 2,
                py: 1.5,
                background: tintBg,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography
                sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 17,
                    fontWeight: 700,
                    lineHeight: "17px",
                    color: colors.txt.primary,
                    mb: "auto",
                }}
            >
                {num}
            </Typography>
            {variant ? (
                <Text variant={variant} semibold>
                    {label}
                </Text>
            ) : (
                <Text light semibold>
                    {label}
                </Text>
            )}
        </Box>
    );

    // vs-last-year and vs-prev-period share a shape: now · same-days comparison · whole period.
    const ComparisonCard = ({ title, period, cmp }) => {
        const partial = detail.window.isPartial;
        const up = (cmp.delta ?? 0) >= 0;
        const base = round1(partial ? cmp.paceTotal : cmp.total);
        return (
            <Box sx={cardSx}>
                <CardHeader
                    title={title}
                    sub={fmtRange(period)}
                    big={pct(cmp.delta) || "–"}
                    bigColor={
                        cmp.delta == null
                            ? undefined
                            : up
                            ? colors.green[400]
                            : colors.red[400]
                    }
                />
                <Box sx={{ mt: 1, display: "flex", gap: 1.5 }}>
                    <MiniBox
                        tintBg={greyTint}
                        num={round1(detail.kpi.currentTotal)}
                        label={partial ? t("so_far") : t("current_period")}
                    />
                    <MiniBox
                        tintBg={up ? colors.green[100] : colors.red[100]}
                        variant={up ? "green" : "red"}
                        num={base}
                        label={partial ? t("same_days") : t("full_period")}
                    />
                    {partial && (
                        <MiniBox
                            tintBg={greyTint}
                            num={round1(cmp.total)}
                            label={t("full_period")}
                        />
                    )}
                </Box>
            </Box>
        );
    };

    const Bars = ({ rows, top, clickable, showRank }) => {
        // A ranking only shows the leaders plus a window around the entity (one
        // neighbour above, the entity, the rows below), so the entity's row index
        // lies about its real position (row 10 can be #27). Resolve a true rank
        // per bar — the entity and the rows below it count from its own rank, the
        // single row just above it is rank-1, the rest are leaders 1..n — so each
        // row shows its rank and we can flag the skipped stretch with a divider.
        const entityIdx = showRank ? rows.findIndex((b) => b.isEntity) : -1;
        const rankOf = (b, i) => {
            if (!showRank) return null;
            if (b.rank != null) return b.rank;
            if (entityIdx === -1) return i + 1;
            if (i >= entityIdx) return detail.ranking.rank + (i - entityIdx);
            if (i === entityIdx - 1) return detail.ranking.rank - 1;
            return i + 1;
        };
        return (
            <Box
                sx={{
                    mt: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                }}
            >
                {rows.map((b, i) => {
                    const pct = Math.max(
                        2,
                        Math.round((b.total / (top || 1)) * 100)
                    );
                    const pinnedHere = pinned.some((p) => p.id === b.id);
                    const fill = b.isEntity
                        ? isDark
                            ? colors.blue[400]
                            : colors.primary[400]
                        : pinnedHere
                        ? isDark
                            ? colors.blue[300]
                            : colors.primary[200]
                        : isDark
                        ? colors.txt["secondary"]
                        : colors.grey[300];
                    const rank = rankOf(b, i);
                    const prevRank = i > 0 ? rankOf(rows[i - 1], i - 1) : 0;
                    const hiddenBefore =
                        rank != null &&
                        prevRank != null &&
                        rank - prevRank > 1;
                    return (
                        <Fragment key={String(b.id)}>
                            {hiddenBefore && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        py: 0.25,
                                        opacity: 0.8,
                                    }}
                                >
                                    <Box sx={{ width: 34 }} />
                                    <Text
                                        light
                                        noBreak
                                        sx={{
                                            width: 180,
                                            fontSize: 14,
                                            fontStyle: "italic",
                                        }}
                                    >
                                        {`#${prevRank + 1} – #${rank - 1}`}
                                    </Text>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            borderBottom: `1px dashed ${
                                                isDark
                                                    ? colors.grey[400]
                                                    : colors.grey[300]
                                            }`,
                                        }}
                                    />
                                    <Box sx={{ width: 56 }} />
                                </Box>
                            )}
                            <Box
                                onClick={
                                    clickable && !b.isEntity
                                        ? () => togglePin(b)
                                        : undefined
                                }
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    cursor:
                                        clickable && !b.isEntity
                                            ? "pointer"
                                            : "default",
                                }}
                            >
                                {showRank && (
                                    <Text
                                        light
                                        sx={{
                                            width: 34,
                                            fontSize: 14,
                                            fontWeight: b.isEntity ? 700 : 400,
                                        }}
                                    >
                                        {`#${rank}`}
                                    </Text>
                                )}
                                <Text
                                    noBreak
                                    sx={{
                                        width: 180,
                                        fontWeight: b.isEntity ? 700 : 400,
                                    }}
                                >
                                    {t(b.name) || b.name}
                                </Text>
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: 16,
                                        background: isDark
                                            ? colors.grey[300]
                                            : colors.grey[100],
                                        borderRadius: 2,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `${pct}%`,
                                            height: 1,
                                            background: fill,
                                            borderRadius: 2,
                                        }}
                                    />
                                </Box>
                                <Text
                                    sx={{
                                        width: 56,
                                        textAlign: "right",
                                        fontSize: 14,
                                        fontWeight: b.isEntity ? 700 : 400,
                                    }}
                                >
                                    {round1(b.total)}
                                </Text>
                            </Box>
                        </Fragment>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box sx={{ display: "flex" }}>
            <Box sx={{ p: 3, flex: 1 }}>
                {/* Header */}
                <Subheading>
                    {t(row.name) || t(row.description) || row.name}
                </Subheading>
                {detail && (
                    <Text light sx={{ fontSize: 13, mt: 0.5 }}>
                        {detail.window.start} – {detail.window.end} •{" "}
                        {round1(detail.kpi.currentTotal)} {t("total").toLowerCase()}
                        {detail.window.isPartial
                            ? ` · ${t("so_far")} (${detail.window.elapsedBuckets}/${
                                detail.window.totalBuckets
                            })`
                            : ""}
                    </Text>
                )}

                {isError && <Text sx={{ mt: 3 }}>{t("something_went_wrong")}</Text>}
                {isFetching && !detail && (
                    <Text light sx={{ mt: 3 }}>
                        {t("loading")}…
                    </Text>
                )}

                {detail && (
                    <>
                        {/* Four cards — one per metric, each shaped for its own data */}
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mt: 2,
                            }}
                        >
                            <ComparisonCard
                                title={t("vs_last_year")}
                                period={periods.lastYear}
                                cmp={detail.kpi.comparisons.lastYear}
                            />
                            <ComparisonCard
                                title={t("vs_prev_period")}
                                period={periods.previous}
                                cmp={detail.kpi.comparisons.previous}
                            />

                            {/* Within period — first half vs second half (daily averages) */}
                            <Box sx={cardSx}>
                                <CardHeader
                                    title={t("across_period")}
                                    sub={t("average")}
                                    big={pct(detail.kpi.across.delta) || "–"}
                                    bigColor={
                                        detail.kpi.across.delta == null
                                            ? undefined
                                            : detail.kpi.across.delta >= 0
                                            ? colors.green[400]
                                            : colors.red[400]
                                    }
                                />
                                <Box sx={{ mt: 1, display: "flex", gap: 1.5,flex:1, }}>
                                    <MiniBox
                                        tintBg={greyTint}
                                        num={round1(detail.kpi.across.firstHalfAvg)}
                                        label={t("first_half")}
                                    />
                                    <MiniBox
                                        tintBg={greyTint}
                                        num={round1(
                                            detail.kpi.across.secondHalfAvg
                                        )}
                                        label={t("second_half")}
                                    />
                                </Box>
                            </Box>

                            {/* On track — run-rate per week / month */}
                            {detail.pace && (
                                <Box sx={cardSx}>
                                    <CardHeader
                                        title={t("on_track")}
                                        sub={fmtRange(periods.current)}
                                        big={`${round1(
                                            detail.pace.projectedTotal
                                        )}`}
                                    />
                                    <Box sx={{ mt: 1, display: "flex", gap: 1.5,flex:1 }}>
                                        <MiniBox
                                            tintBg={isDark ? colors.blue[100]  : colors.primary[100] }
                                            variant={isDark ? "blue" : "primary"}
                                            num={`${round1(detail.pace.perWeek)}`}
                                            label={t("weekly")}
                                        />
                                        <MiniBox
                                            tintBg={greyTint}
                                            num={`${round1(detail.pace.perMonth)}`}
                                            label={t("monthly")}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Overlaid daily chart + compare picker */}
                        <Box 
                            sx={{
                                mt:3,
                                display:'flex',
                                gap:2,
                            }}
                        >
                            <Box
                                sx={{
                                    flex:2,
                                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                    p:3,
                                    pr:0,
                                    borderRadius:3,
                                    display:'flex',
                                    flexDirection:"column",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Subheading >{t("daily_orders")}</Subheading>
                                    {pinOptions.length > 0 && (
                                        <DropdownBaseV2
                                            options={pinOptions}
                                            displayName="name"
                                            valueKey="id"
                                            multiple
                                            searchOptions={{ enabled: true }}
                                            selected={pinned.map((p) => p.id)}
                                            onChange={(ids) =>
                                                syncPins(Array.isArray(ids) ? ids : [])
                                            }
                                        >
                                            {({ onClick }) => (
                                                <ButtonBase
                                                    onClick={onClick}
                                                    sx={{
                                                        height: 36,
                                                        px: 1.5,
                                                        gap: 1,
                                                        borderRadius: 4,
                                                        background: cardBg,
                                                        color: colors.txt.primary,
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {t("compare_relations")}
                                                    {pinned.length
                                                        ? ` (${pinned.length})`
                                                        : ""}
                                                    <KeyboardArrowDown fontSize="small" />
                                                </ButtonBase>
                                            )}
                                        </DropdownBaseV2>
                                    )}
                                </Box>
                                {pinned.length > 0 && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            mt: 1,
                                        }}
                                    >
                                        {pinned.map((p) => (
                                            <Box
                                                key={p.id}
                                                onClick={() => togglePin(p)}
                                                sx={{
                                                    cursor: "pointer",
                                                    px: 1.5,
                                                    height: 28,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    borderRadius: 14,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    background: cardBg,
                                                    color: colors.txt.primary,
                                                }}
                                            >
                                                {p.name}{" "}
                                                <span style={{ opacity: 0.6 }}>×</span>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                <Box sx={{ mt: "auto" }}>
                                    <LineChartContentReports
                                        color="brown"
                                        staticColors={chartColors}
                                        values={chartSeries}
                                        categories={detail.series.labels}
                                        isLoading={false}
                                        isError={false}
                                    />
                                </Box>
                            </Box>

                            {/* Weekday pattern */}
                            {weekday && (
                                <Box
                                    sx={{
                                        flex:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        p:2,
                                        borderRadius:3,
                                        display:"flex",
                                        flexDirection:"column",
                                    }}
                                >
                                    <Subheading>{t("by_weekday")}</Subheading>
                                
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 2,
                                            mt: 3,
                                            alignItems: "stretch",
                                            // height: 200,
                                            flex:1,
                                        }}
                                    >
                                        {weekday.avgs.map((a, wd) => (
                                            <Box
                                                key={wd}
                                                sx={{
                                                    flex: 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Text bold>
                                                    {a}
                                                </Text>
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        flex: 1,
                                                        display: "flex",
                                                        alignItems: "flex-end",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: "100%",
                                                            height: `${Math.round((a / weekday.max) * 100)}%`,
                                                            minHeight: 2,
                                                            borderRadius: 1,
                                                            background:isDark ? colors.grey[400] : colors.grey[300],
                                                        }}
                                                    />
                                                </Box>
                                                <Text light sx={{ fontSize: 14 }}>
                                                    {weekdayName(wd, i18n.language)}
                                                </Text>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Box
                            sx={{
                                display:"flex",
                                gap:2,
                            }}
                        >
                            {/* Ranking */}
                            {detail.ranking?.bars?.length > 0 && (
                                <Box
                                    sx={{
                                        flex:1,
                                        mt: 3,
                                        background: isDark
                                            ? colors.grey[200]
                                            : colors.grey[0],
                                        p: 3,
                                        borderRadius: 3,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Subheading >
                                        {t("how_does_it_compare")}
                                    </Subheading>
                                    {/* Position and total volume, above the bars */}
                                    <Box
                                        sx={{
                                            my: 2,
                                            display: "flex",
                                            gap: 1.5,
                                            width:1,
                                        }}
                                    >
                                        <MiniBox
                                            tintBg={greyTint}
                                            num={`#${detail.ranking.rank ?? "–"}`}
                                            label={`${t("of")} ${detail.ranking.of}`}
                                        />
                                        <MiniBox
                                            tintBg={
                                                isDark
                                                    ? colors.blue[100]
                                                    : colors.primary[100]
                                            }
                                            variant={isDark ? "blue" : "primary"}
                                            num={round1(
                                                detail.ranking.bars.find(
                                                    (b) => b.isEntity
                                                )?.total ?? detail.kpi.currentTotal
                                            )}
                                            label={
                                                detail.ranking.share != null
                                                    ? `${detail.ranking.share}% ${t(
                                                        "share_of_volume"
                                                    )}`
                                                    : t("volume")
                                            }
                                        />
                                    </Box>

                                    <Bars
                                        rows={detail.ranking.bars}
                                        top={detail.ranking.bars[0]?.total}
                                        clickable
                                        showRank
                                    />
                                </Box>
                            )}


                            {/* Secondary-dimension breakdown */}
                            {breakdownOptions.length > 0 && (
                                <Box 
                                    sx={{ 
                                        mt: 3,
                                        flex:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        p:2,
                                        borderRadius:3,
                                        display:"flex",
                                        flexDirection:"column",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent:'space-between',
                                            gap: 2,
                                            flexWrap: "wrap",
                                            mb:1,
                                        }}
                                    >
                                        <Subheading>
                                            {t("breakdown_by")}
                                        </Subheading>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                gap: 0.5,
                                                p: 0.5,
                                                borderRadius: 4,
                                                background: cardBg,
                                            }}
                                        >
                                            {breakdownOptions.map((dim) => {
                                                const active =
                                                    (detail.breakdown?.dimension ||
                                                        breakdownOptions[0]) === dim;
                                                return (
                                                    <Box
                                                        key={dim}
                                                        onClick={() =>
                                                            setSecondaryDimension(dim)
                                                        }
                                                        sx={{
                                                            cursor: "pointer",
                                                            px: 1.5,
                                                            height: 28,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            borderRadius: 12,
                                                            fontSize: 12,
                                                            fontWeight: active
                                                                ? 700
                                                                : 500,
                                                            background: active
                                                                ? isDark
                                                                    ? colors.blue[100]
                                                                    : colors
                                                                        .primary[100]
                                                                : "transparent",
                                                            color: active
                                                                ? isDark
                                                                    ? colors.blue[400]
                                                                    : colors
                                                                        .primary[400]
                                                                : colors.txt.secondary,
                                                        }}
                                                    >
                                                        {t(dim)}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                    {detail.breakdown?.rows?.length > 0 && (
                                        <Bars
                                            rows={detail.breakdown.rows}
                                            top={detail.breakdown.rows[0]?.total}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </Box>
            {detail && <RhCrossDomainSidebar crossDomain={detail.crossDomain} />}
        </Box>
    );
};
